/**
 * Responsibility: Implement default remediations behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type {
  AttributeKey,
  GameState,
  InventoryItem,
  LivingEntityStats,
  NPC,
  QuestStatus,
  WorldNPC,
} from "@anyquest/shared-core"
import { totalMinFromClock } from '../../temporal'
import { generateNpcId } from '../../state'
import { normalizeVitals } from '../../vitals'
import { isMetaSystemNpcName } from '../../npc-guard'
import { buildAtlasComponentReport } from '../../atlas-graph'
import type { SupervisorIncident, SupervisorRemediationResult } from "@anyquest/shared-core"
import { captureRollbackSnapshot, discardRollbackSnapshot } from '../rollback'

const VALID_ATTRS = new Set<AttributeKey>(['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA', 'PER', 'LCK', 'WIL', 'FOR', 'AGI', 'PRS'])
const VALID_QUEST_STATUS = new Set<QuestStatus>(['active', 'completed', 'failed', 'dormant', 'potential'])

function normalizeInventoryItems(items: InventoryItem[] | undefined): { next: InventoryItem[]; changed: boolean } {
  const list = Array.isArray(items) ? items : []
  const byName = new Map<string, InventoryItem>()
  let changed = !Array.isArray(items)

  for (const raw of list) {
    const name = String(raw?.name ?? '').trim()
    if (!name) {
      changed = true
      continue
    }
    const key = name.toLowerCase()
    const equipped = !!raw?.equipped
    const existing = byName.get(key)
    if (!existing) {
      byName.set(key, {
        name,
        equipped,
        ...(raw?.desc ? { desc: String(raw.desc).trim() } : {}),
        ...(raw?.icon ? { icon: String(raw.icon).trim() } : {}),
        ...(typeof raw?.worth === 'number' ? { worth: raw.worth } : {}),
        ...(Array.isArray(raw?.actions) ? { actions: raw.actions } : {}),
      })
      if (!raw || raw.name !== name || raw.equipped !== equipped) changed = true
      continue
    }
    if (equipped && !existing.equipped) {
      existing.equipped = true
      changed = true
    }
    changed = true
  }

  return { next: [...byName.values()], changed }
}

function normalizeNameList(items: string[] | undefined): { next: string[]; changed: boolean } {
  const list = Array.isArray(items) ? items : []
  const next: string[] = []
  const seen = new Set<string>()
  let changed = !Array.isArray(items)

  for (const raw of list) {
    const value = String(raw ?? '').trim()
    if (!value) {
      changed = true
      continue
    }
    const key = value.toLowerCase()
    if (seen.has(key)) {
      changed = true
      continue
    }
    seen.add(key)
    next.push(value)
    if (raw !== value) changed = true
  }

  return { next, changed }
}

function normalizeEntityModifiers(entity: LivingEntityStats, totalMin: number): boolean {
  let changed = false
  if (!entity.dormantEffects) {
    entity.dormantEffects = []
    changed = true
  }

  const nextModifiers: LivingEntityStats['modifiers'] = {}
  for (const [rawKey, rawMods] of Object.entries(entity.modifiers ?? {})) {
    const key = rawKey as AttributeKey
    if (!VALID_ATTRS.has(key)) {
      changed = true
      continue
    }
    const mods = Array.isArray(rawMods) ? rawMods : []
    const active: NonNullable<LivingEntityStats['modifiers'][AttributeKey]> = []
    for (const mod of mods) {
      const valid = Number.isFinite(mod?.val)
        && !!String(mod?.label ?? '').trim()
        && !!String(mod?.source ?? '').trim()
      if (!valid) {
        changed = true
        continue
      }
      if (mod.expiresAt != null && mod.expiresAt <= totalMin) {
        entity.dormantEffects.push({
          label: String(mod.label),
          source: String(mod.source),
          expiredAt: mod.expiresAt,
          attr: key,
        })
        changed = true
        continue
      }
      active.push({
        val: Number(mod.val),
        label: String(mod.label),
        source: String(mod.source),
        ...(Number.isFinite(mod.expiresAt) ? { expiresAt: Number(mod.expiresAt) } : {}),
      })
    }
    if (active.length > 0) nextModifiers[key] = active
    if (active.length !== mods.length) changed = true
  }

  entity.modifiers = nextModifiers
  if ((entity.dormantEffects?.length ?? 0) > 60) {
    entity.dormantEffects = [...(entity.dormantEffects ?? [])]
      .sort((a, b) => b.expiredAt - a.expiredAt)
      .slice(0, 60)
    changed = true
  }
  return changed
}

function normalizeNpc(npc: NPC): NPC | null {
  const name = String(npc?.name ?? '').trim()
  const identity = String(npc?.identity ?? '').trim()
  if (!name || !identity) return null
  if (isMetaSystemNpcName(name)) return null

  const inv = normalizeNameList(npc.inventory)
  return {
    ...npc,
    name,
    identity,
    inventory: inv.next,
    relationship: Number.isFinite(npc.relationship) ? Number(npc.relationship) : 0,
    memory: Array.isArray(npc.memory)
      ? npc.memory.map(v => String(v ?? '').trim()).filter(Boolean).slice(-10)
      : [],
    intentions: Array.isArray(npc.intentions)
      ? npc.intentions.map(v => String(v ?? '').trim()).filter(Boolean).slice(0, 8)
      : [],
    activeEffects: Array.isArray(npc.activeEffects)
      ? npc.activeEffects.map(v => String(v ?? '').trim()).filter(Boolean).slice(0, 8)
      : [],
    dormantEffects: Array.isArray(npc.dormantEffects)
      ? npc.dormantEffects.slice(-60)
      : [],
  }
}

function normalizeWorldNpc(npc: WorldNPC, fallbackLocation: string): WorldNPC | null {
  const name = String(npc?.name ?? '').trim()
  const id = String(npc?.id ?? '').trim() || generateNpcId(name || 'npc')
  const location = String(npc?.location ?? '').trim() || fallbackLocation
  if (!name || !location) return null
  if (isMetaSystemNpcName(name)) return null

  const inventory = normalizeNameList(npc.inventory)

  return {
    ...npc,
    id,
    name,
    identity: String(npc?.identity ?? '').trim() || name,
    lore: String(npc?.lore ?? '').trim() || `${name} — ${String(npc?.identity ?? '').trim() || 'Unknown'}`,
    location,
    inventory: inventory.next,
    intentions: Array.isArray(npc.intentions)
      ? npc.intentions.map(v => String(v ?? '').trim()).filter(Boolean).slice(0, 8)
      : [],
    feelings: npc.feelings && typeof npc.feelings === 'object' ? npc.feelings : {},
    relationship: Number.isFinite(npc.relationship) ? Number(npc.relationship) : 0,
    memory: Array.isArray(npc.memory)
      ? npc.memory.map(v => String(v ?? '').trim()).filter(Boolean).slice(-10)
      : [],
    activeEffects: Array.isArray(npc.activeEffects)
      ? npc.activeEffects.map(v => String(v ?? '').trim()).filter(Boolean).slice(0, 8)
      : [],
    dormantEffects: Array.isArray(npc.dormantEffects)
      ? npc.dormantEffects.slice(-60)
      : [],
  }
}

function hasNode(state: GameState, key: string): boolean {
  return !!state.atlas.nodes[key]
}

function normalizeRoomNpcs(state: GameState): boolean {
  const list = Array.isArray(state.currentRoom.npcs) ? state.currentRoom.npcs : []
  const seen = new Set<string>()
  const next: NPC[] = []
  let changed = !Array.isArray(state.currentRoom.npcs)

  for (const raw of list) {
    const normalized = normalizeNpc(raw)
    if (!normalized) {
      changed = true
      continue
    }
    const key = normalized.name.toLowerCase()
    if (seen.has(key)) {
      changed = true
      continue
    }
    seen.add(key)
    next.push(normalized)
    if (raw !== normalized) changed = true
  }

  state.currentRoom.npcs = next
  return changed
}

function normalizeWorldNpcs(state: GameState, fallbackLocation: string): boolean {
  const list = Array.isArray(state.world.npcs) ? state.world.npcs : []
  const byId = new Map<string, WorldNPC>()
  let changed = !Array.isArray(state.world.npcs)

  for (const raw of list) {
    const normalized = normalizeWorldNpc(raw, fallbackLocation)
    if (!normalized) {
      changed = true
      continue
    }

    if (!hasNode(state, normalized.location)) {
      normalized.location = fallbackLocation
      changed = true
    }

    let candidateId = normalized.id
    while (byId.has(candidateId)) {
      candidateId = generateNpcId(normalized.name)
      changed = true
    }
    if (candidateId !== normalized.id) normalized.id = candidateId

    byId.set(normalized.id, normalized)
    if (raw !== normalized) changed = true
  }

  state.world.npcs = [...byId.values()]
  return changed
}

function ensurePlayerNode(state: GameState, playerNodeKey: string): boolean {
  if (state.atlas.nodes[playerNodeKey]) return false
  state.atlas.nodes[playerNodeKey] = {
    name: String(state.currentRoom?.name ?? '').trim() || 'Unknown Location',
    desc: String(state.currentRoom?.desc ?? '').trim(),
    items: [...(state.currentRoom?.items ?? [])],
    flags: { ...(state.currentRoom?.flags ?? {}) },
    dormantFlags: [...(state.currentRoom?.dormantFlags ?? [])],
  }
  return true
}

function pruneInvalidEdges(state: GameState): boolean {
  const before = state.atlas.edges.length
  state.atlas.edges = (state.atlas.edges ?? []).filter(edge => {
    const left = hasNode(state, `${edge.x1}_${edge.y1}_${edge.z1 ?? 0}`)
    const right = hasNode(state, `${edge.x2}_${edge.y2}_${edge.z2 ?? 0}`)
    return left && right
  })
  return state.atlas.edges.length !== before
}

function createAtlasFrontier(state: GameState, playerNodeKey: string): boolean {
  const report = buildAtlasComponentReport(state.atlas, playerNodeKey)
  const candidate = report.suggestedExpansion
  if (!candidate || state.atlas.nodes[candidate.toKey]) return false

  const sourceNode = state.atlas.nodes[candidate.fromKey]
  const direction = String(candidate.direction || 'unknown')
  const title = `${direction.replace(/^./, (letter) => letter.toUpperCase())} Passage`
  state.atlas.nodes[candidate.toKey] = {
    name: title,
    desc: `A newly opened ${direction} route extends beyond ${String(sourceNode?.name ?? 'the known map')}.`,
    items: [],
    flags: { presentation: 'passage' },
    dormantFlags: [],
    clocks: [],
  }

  const parts1 = candidate.fromKey.split('_').map((value) => Number.parseInt(value, 10) || 0)
  const parts2 = candidate.toKey.split('_').map((value) => Number.parseInt(value, 10) || 0)
  const [x1, y1, z1] = parts1
  const [x2, y2, z2] = parts2
  
  const edgeExists = (state.atlas.edges ?? []).some((edge) => {
    const ez1 = edge.z1 ?? 0
    const ez2 = edge.z2 ?? 0
    return ((edge.x1 === x1 && edge.y1 === y1 && ez1 === z1 && edge.x2 === x2 && edge.y2 === y2 && ez2 === z2)
    || (edge.x1 === x2 && edge.y1 === y2 && ez1 === z2 && edge.x2 === x1 && edge.y2 === y1 && ez2 === z1))
  })
  if (!edgeExists) state.atlas.edges.push({ x1, y1, z1, x2, y2, z2 })
  return true
}

function segmentLongEdges(state: GameState): boolean {
  const edges = state.atlas?.edges ?? []
  let changed = false
  const nextEdges: typeof edges = []

  for (const edge of edges) {
    const dx = edge.x2 - edge.x1
    const dy = edge.y2 - edge.y1
    const dz = (edge.z2 ?? 0) - (edge.z1 ?? 0)
    const dist = Math.abs(dx) + Math.abs(dy) + Math.abs(dz)

    if (dist <= 1) {
      nextEdges.push(edge)
      continue
    }

    // Insert waypoint node
    let wx = edge.x1
    let wy = edge.y1
    let wz = edge.z1 ?? 0

    if (Math.abs(dx) > 0) wx += Math.sign(dx)
    else if (Math.abs(dy) > 0) wy += Math.sign(dy)
    else if (Math.abs(dz) > 0) wz += Math.sign(dz)

    const wKey = `${wx}_${wy}_${wz}`
    if (!state.atlas.nodes[wKey]) {
      state.atlas.nodes[wKey] = {
        name: 'Transitional Path',
        desc: 'A waypoint between established sites.',
        flags: { presentation: 'passage' },
        items: [],
        dormantFlags: [],
      }
    }

    // Split edge into two
    nextEdges.push({ x1: edge.x1, y1: edge.y1, z1: edge.z1, x2: wx, y2: wy, z2: wz, risk: edge.risk })
    nextEdges.push({ x1: wx, y1: wy, z1: wz, x2: edge.x2, y2: edge.y2, z2: edge.z2, risk: edge.risk })
    changed = true
  }

  if (changed) state.atlas.edges = nextEdges
  return changed
}

function normalizeQuests(state: GameState, nowMin: number): boolean {
  const seen = new Set<string>()
  let changed = false
  const next = []

  for (const quest of state.journal ?? []) {
    const id = String(quest?.id ?? '').trim()
    if (!id) {
      changed = true
      continue
    }
    if (seen.has(id)) {
      changed = true
      continue
    }
    seen.add(id)

    let status = quest.status
    if (!VALID_QUEST_STATUS.has(status)) {
      status = 'dormant'
      changed = true
    }

    const dependsOn = Array.isArray(quest.dependsOn)
      ? quest.dependsOn.map(dep => String(dep ?? '').trim()).filter(dep => !!dep && dep !== id)
      : undefined
    if ((dependsOn?.length ?? 0) !== (quest.dependsOn?.length ?? 0)) changed = true

    let completed = quest.completed
    if ((status === 'completed' || status === 'failed') && !Number.isFinite(completed)) {
      completed = nowMin
      changed = true
    }

    next.push({
      ...quest,
      id,
      status,
      ...(dependsOn?.length ? { dependsOn } : {}),
      ...(Number.isFinite(completed) ? { completed } : {}),
    })
  }

  state.journal = next
  return changed
}

function normalizeCombat(state: GameState): boolean {
  if (!state.combat) return false
  let changed = false
  const combat = state.combat

  if (combat.status === 'active') {
    const npcName = String(combat.npc?.name ?? '').trim()
    if (!npcName) {
      state.combat = null
      return true
    }
    const roomNpc = (state.currentRoom.npcs ?? []).find(n => n.name === npcName)
    if (!roomNpc) {
      const playerLoc = `${state.player.location.x}_${state.player.location.y}_${state.player.location.z ?? 0}`
      const worldNpc = (state.world.npcs ?? []).find(n => n.name === npcName && n.location === playerLoc)
      if (worldNpc) {
        state.currentRoom.npcs.push({
          name: worldNpc.name,
          identity: worldNpc.identity,
          lore: worldNpc.lore,
          sex: worldNpc.sex,
          intentions: worldNpc.intentions,
          feelings: worldNpc.feelings,
          relationship: worldNpc.relationship,
          memory: worldNpc.memory,
          inventory: worldNpc.inventory ?? [],
          activeEffects: worldNpc.activeEffects ?? [],
          dormantEffects: worldNpc.dormantEffects ?? [],
        })
        changed = true
      } else {
        state.combat = null
        return true
      }
    }

    if (!Number.isFinite(combat.round) || combat.round < 1) {
      combat.round = 1
      changed = true
    }
  }

  return changed
}

function normalizeStateByIncident(state: GameState, incident: SupervisorIncident): boolean {
  const playerNodeKey = `${state.player.location.x}_${state.player.location.y}_${state.player.location.z ?? 0}`
  const nowMin = totalMinFromClock(state.world.clock)
  let changed = false

  switch (incident.code) {
    case 'actor_player_identity_missing': {
      const fallback = String(state.meta?.name ?? '').trim() || 'Unknown Traveler'
      if (state.player.identity !== fallback) {
        state.player.identity = fallback
        changed = true
      }
      return changed
    }
    case 'actor_player_vitals_out_of_range': {
      const next = normalizeVitals(state.player.vitals, state.player.attributes, state.player.vitals)
      if (next.vitality !== state.player.vitals.vitality
        || next.spirit !== state.player.vitals.spirit
        || next.essence !== state.player.vitals.essence
        || next.maxVitality !== state.player.vitals.maxVitality
        || next.maxSpirit !== state.player.vitals.maxSpirit
        || next.maxEssence !== state.player.vitals.maxEssence) {
        state.player.vitals = next
        changed = true
      }
      return changed
    }
    case 'actor_invalid_companion_entries': {
      const before = state.companions.length
      state.companions = (state.companions ?? []).filter(c => {
        const name = String(c?.name ?? '').trim()
        const identity = String(c?.identity ?? '').trim()
        return !!name && !!identity
      })
      return state.companions.length !== before
    }
    case 'inventory_player_items_invalid':
    case 'inventory_player_duplicate_names': {
      const normalized = normalizeInventoryItems(state.player.inventory)
      state.player.inventory = normalized.next
      return normalized.changed
    }
    case 'inventory_npc_items_invalid': {
      for (const npc of state.currentRoom.npcs ?? []) {
        const inv = normalizeNameList(npc.inventory)
        npc.inventory = inv.next
        changed = changed || inv.changed
      }
      for (const npc of state.world.npcs ?? []) {
        const inv = normalizeNameList(npc.inventory)
        npc.inventory = inv.next
        changed = changed || inv.changed
      }
      return changed
    }
    case 'atlas_player_node_missing':
      return ensurePlayerNode(state, playerNodeKey)
    case 'atlas_invalid_edges':
      return pruneInvalidEdges(state)
    case 'atlas_component_sealed':
      return createAtlasFrontier(state, playerNodeKey)
    case 'atlas_long_passages':
      return segmentLongEdges(state)
    case 'atlas_current_room_name_missing': {
      const nodeName = String(state.atlas.nodes[playerNodeKey]?.name ?? '').trim()
      const fallback = nodeName || 'Unknown Location'
      if (String(state.currentRoom.name ?? '').trim() !== fallback) {
        state.currentRoom.name = fallback
        return true
      }
      return false
    }
    case 'atlas_world_npc_location_missing_node': {
      for (const npc of state.world.npcs ?? []) {
        const key = String(npc.location ?? '').trim()
        if (!key || !state.atlas.nodes[key]) {
          npc.location = playerNodeKey
          changed = true
        }
      }
      return changed
    }
    case 'combat_npc_name_missing':
    case 'combat_opponent_missing_from_room':
    case 'combat_round_invalid':
      return normalizeCombat(state)
    case 'quest_duplicate_ids':
    case 'quest_invalid_status':
    case 'quest_terminal_missing_completed_timestamp':
    case 'quest_dangling_dependencies':
      return normalizeQuests(state, nowMin)
    case 'effect_expired_modifiers_present':
    case 'effect_malformed_modifiers':
    case 'effect_dormant_effects_oversized': {
      changed = normalizeEntityModifiers(state.player, nowMin) || changed
      for (const companion of state.companions ?? []) {
        changed = normalizeEntityModifiers(companion, nowMin) || changed
      }
      return changed
    }
    case 'persistence_meta_timestamp_invalid': {
      state.meta.timestamp = Date.now()
      return true
    }
    case 'persistence_world_log_oversized': {
      const before = state.worldLog.length
      state.worldLog = (state.worldLog ?? []).slice(-30)
      return state.worldLog.length !== before
    }
    case 'persistence_history_oversized': {
      const before = state.history.length
      state.history = (state.history ?? []).slice(-500)
      return state.history.length !== before
    }
    case 'persistence_world_last_tick_ahead_of_clock': {
      const next = Math.min(Number(state.world.lastTick ?? 0), nowMin)
      if (next !== state.world.lastTick) {
        state.world.lastTick = next
        return true
      }
      return false
    }
    case 'object_room_items_invalid':
    case 'object_room_items_duplicate': {
      const norm = normalizeNameList(state.currentRoom.items)
      state.currentRoom.items = norm.next
      return norm.changed
    }
    case 'object_room_npcs_invalid':
    case 'object_room_npcs_duplicate_names':
      return normalizeRoomNpcs(state)
    case 'object_world_npcs_invalid':
    case 'object_world_npcs_duplicate_ids':
      return normalizeWorldNpcs(state, playerNodeKey)
    default:
      return false
  }
}

export function applySupervisorRemediation(
  state: GameState,
  incident: SupervisorIncident,
): SupervisorRemediationResult {
  const snapshotId = captureRollbackSnapshot(state, incident)
  const changed = normalizeStateByIncident(state, incident)
  if (!changed) {
    discardRollbackSnapshot(snapshotId)
    return {
      applied: false,
      changed: false,
      message: 'No state mutation was required for this incident.',
    }
  }

  return {
    applied: true,
    changed: true,
    rollbackSnapshotId: snapshotId,
    message: `Applied remediation for ${incident.code}.`,
  }
}
